import Add16 from '@carbon/icons-react/es/add/16';
import Calendar24 from '@carbon/icons-react/es/calendar/24';
import Task20 from '@carbon/icons-react/es/task/20';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { Project } from 'api/project';
import { Task, TaskAPI, TaskType } from 'api/tasks';
import { TimesheetAPI, TimesheetEntryType } from 'api/timesheets';
import { EnabledSourcesConfiguration } from 'api/timesheets/configuration/configuration';
import { ReactComponent as Activity24Icon } from 'assets/svgs/activity.svg';
import { VirtualizedMenuList } from 'components/atoms/creatable-virtualised-list';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover } from 'components/atoms/popover';
import { CreatableSelect } from 'components/atoms/select';
import { isRelatedEl } from 'components/molecules/new-task-popup/is-related-element';
import { StatusIndicatorIcon } from 'components/molecules/status-indicator';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { EntityOption } from '../../drawers/entity-option';
import { TimesheetTableTimeEntry, TimesheetWeeklyRowTask } from '../../types';
import { checkTimeEntryConfig, getMinutes } from '../time-entry-utils';
import { NameCellWithTooltip, StatusIndicatorWrapper } from './name-cell';

export const SelectContainer = styled.div`
  min-height: 350px;
  width: 272px;
  display: flex;
  flex-direction: column;
`;

const TaskIcon = styled(Task20)`
  position: relative;
  top: var(--spacing-01);
  width: calc(var(--spacing-05) + var(--spacing-01));
`;

export const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 'var(--spacing-05)',
  }),
  menu: () => ({ boxShadow: 'none' }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
};

const PlaceholderTask = styled.div`
  display: flex;
  align-items: center;
  padding: calc(calc(1 * var(--spacing-05)) - 1px) var(--spacing-05);
  color: var(--primary-color);
  border-right: 1px solid var(--table-border-color);
`;

const IconShell = styled.div`
  display: flex;
  align-items: center;

  > svg {
    margin-right: var(--spacing-04);
  }
`;

export const WrapperCell = styled.div<{
  isNoEntriesSet: boolean;
}>`
  cursor: ${({ isNoEntriesSet }) => isNoEntriesSet && 'pointer'};
`;

export const getTaskCell = (
  task: TimesheetWeeklyRowTask,
  minutes: number,
  placeHolderText: () => string,
  isOnlyProjectsEnabled: boolean,
  isProjectOwnerApproval?: boolean,
) => {
  switch (task.entryType) {
    case TimesheetEntryType.TaskEntry:
      return (
        <NameCellWithTooltip
          logo={
            <StatusIndicatorWrapper>
              <StatusIndicatorIcon
                group={task.status}
                type={task.type}
                progress={task.progress}
              />
            </StatusIndicatorWrapper>
          }
          primaryText={task.name}
        />
      );
    case TimesheetEntryType.Integration:
      return (
        <NameCellWithTooltip
          logo={<Calendar24 />}
          primaryText={task.activityName}
        />
      );
    case TimesheetEntryType.AdhocEntry:
      return (
        <NameCellWithTooltip
          logo={<Activity24Icon />}
          primaryText={task.activityName}
        />
      );
    case TimesheetEntryType.PhaseEntry:
      return (
        <NameCellWithTooltip
          logo={
            <StatusIndicatorWrapper>
              <StatusIndicatorIcon
                group={task.status as number}
                type={TaskType.Task}
              />
            </StatusIndicatorWrapper>
          }
          primaryText={task.projectPhase.name}
        />
      );
    default:
      if (minutes === 0) {
        if (isOnlyProjectsEnabled) {
          return <></>;
        }

        return (
          <PlaceholderTask data-cy="timesheet-work-placeholder">
            <IconShell>
              <Add16 />
            </IconShell>
            {task.project?.id === 1 || !task.project?.id ? (
              <FormattedMessage id="timesheets.buttons.add_activity" />
            ) : (
              <span>{placeHolderText()}</span>
            )}
          </PlaceholderTask>
        );
      }

      if (isProjectOwnerApproval) {
        return (
          <NameCellWithTooltip
            logo={<TaskIcon />}
            primaryText={
              <FormattedMessage id="timesheets.reported.on.project" />
            }
          />
        );
      }

      return null;
  }
};

export const TaskNameCell: FC<{
  task: TimesheetWeeklyRowTask;
  onActivate?: (isActive: boolean) => void;
  addEntityToProject?: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    entity: any,
  ) => void;
  days: Record<string, TimesheetTableTimeEntry[]>;
  timesheetEntriesConfig: EnabledSourcesConfiguration;
  addActivityToProject?: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    activityName: string,
  ) => void;
  projects: Project[] | undefined;
  isProjectOwnerApproval?: boolean;
}> = ({
  task,
  onActivate,
  addEntityToProject,
  timesheetEntriesConfig,
  days,
  addActivityToProject,
  projects,
  isProjectOwnerApproval,
}) => {
  const [visible, setVisible] = useState(false);
  const [taskList, setTaskList] = useState<Task[]>();
  const [milestoneList, setMilestoneList] = useState<Task[]>();
  const [activityList, setActivityList] = useState<string[]>();
  const [searchedText, setSearchedText] = useState<string>('');
  const translate = useTranslate();
  const intl = useIntl();

  const project = projects?.find(
    (x: Project) => x.projectId === task.project?.id,
  );

  const { isOnlyProjectsEnabled } = checkTimeEntryConfig(
    timesheetEntriesConfig,
  );

  const minutes = getMinutes(days);

  useEffect(() => {
    if (minutes === 0 && timesheetEntriesConfig.TASK) {
      TaskAPI.getTasksAndMilestonesForProjects(Number(project?.projectId)).then(
        (resp) => setTaskList(resp as Task[]),
      );
    }
  }, [project?.projectId, timesheetEntriesConfig.TASK, minutes]);

  useEffect(() => {
    if (minutes === 0 && timesheetEntriesConfig.MILESTONE) {
      TaskAPI.getTasksAndMilestonesForProjects(
        Number(project?.projectId),
        true,
      ).then((resp) => setMilestoneList(resp as Task[]));
    }
  }, [project?.projectId, timesheetEntriesConfig.MILESTONE, minutes]);

  const {
    user: { userId },
  } = useUser();

  useEffect(() => {
    if (minutes === 0 && timesheetEntriesConfig.ADHOC) {
      TimesheetAPI.getActivitiesOfUserInProject(userId, 'all').then(
        (uniqueActivityList) => setActivityList(uniqueActivityList),
      );
    }
  }, [userId, timesheetEntriesConfig.ADHOC, minutes]);

  const entityOptions: any[] = [];

  if (timesheetEntriesConfig.PHASE) {
    const projectPhaseOptions = project?.projectPhases?.map((phase) => ({
      label: phase.projectPhaseName,
      value: phase.projectPhaseId,
      type: 'PHASE_ENTRY',
      extra: {
        ...phase,
        project: {
          id: project.projectId,
          name: project.projectName,
        },
      },
    }));

    if ((projectPhaseOptions ?? [])?.length > 0) {
      const projectPhasesObject = {
        label: 'Phases',
        options: projectPhaseOptions,
      };

      entityOptions.push(projectPhasesObject);
    }
  }

  if (timesheetEntriesConfig.MILESTONE) {
    if ((milestoneList ?? [])?.length > 0) {
      const projectMilestoneOptions = {
        label: 'Milestones',
        options: milestoneList?.map((t) => ({
          label: t.taskName,
          value: t.taskId,
          type: 'MILESTONE_ENTRY',
          extra: { ...t },
        })),
      };

      entityOptions.push(projectMilestoneOptions);
    }
  }

  if (timesheetEntriesConfig.TASK) {
    if ((taskList ?? [])?.length > 0) {
      const taskListWithoutMilestones = taskList?.filter(
        (t) => t.type !== TaskType.Milestone,
      );

      if ((taskListWithoutMilestones ?? [])?.length > 0) {
        const projectTaskOptions = {
          label: 'Tasks',
          options: taskListWithoutMilestones?.map((t) => ({
            label: t.taskName,
            value: t.taskId,
            type: 'TASK_ENTRY',
            extra: { ...t },
          })),
        };

        entityOptions.push(projectTaskOptions);
      }
    }
  }

  if (timesheetEntriesConfig.ADHOC) {
    if ((activityList ?? [])?.length > 0) {
      const projectActivityOptions = {
        label: 'Activities',
        options: activityList?.map((activityName) => ({
          label: activityName,
          value: activityName,
          type: 'ACTIVITY_ENTRY',
        })),
      };

      entityOptions.push(projectActivityOptions);
    }
  }

  const handleVisibilityToggle = (currentVisibility: boolean) => {
    setVisible(currentVisibility);
  };
  const onSelection = (props) => {
    if (props.type === 'ACTIVITY_ENTRY') {
      addActivityToProject?.(task, days, props.label);
    } else {
      addEntityToProject?.(task, days, props);
    }
    handleVisibilityToggle(false);
  };

  const onCreateOption = useCallback(
    (props) => {
      addActivityToProject?.(task, days, props);
      handleVisibilityToggle(false);
    },
    [addActivityToProject, days, task],
  );

  useEffect(() => onActivate?.(visible), [onActivate, visible]);

  const placeHolderText = () => {
    const options: string[] = [];

    if (timesheetEntriesConfig.PHASE) {
      options.push('phase');
    }
    if (timesheetEntriesConfig.TASK || timesheetEntriesConfig.MILESTONE) {
      options.push('task');
    }
    if (timesheetEntriesConfig.ADHOC) {
      options.push('activity');
    }

    return `Add ${intl.formatList(options, { type: 'unit' })}`;
  };

  const openTaskPopup = useCallback(() => {
    if (minutes === 0) {
      setVisible(true);
    }
  }, [minutes]);

  return (
    <OutsideClickHandler
      allowed={isRelatedEl}
      onOutsideClick={() => handleVisibilityToggle(false)}
    >
      <Popover
        placement="bottomLeft"
        visible={visible}
        content={
          <SelectContainer data-cy="timesheet-work-select">
            <CreatableSelect
              menuIsOpen
              autoFocus
              components={{
                ClearIndicator: null,
                IndicatorSeparator: null,
                Option: EntityOption,
                MenuList: VirtualizedMenuList,
              }}
              isClearable
              placeholder={<FormattedMessage id="placeholder.search" />}
              tabSelectsValue={false}
              formatCreateLabel={(inputValue: string) =>
                translate('timesheets.adhoc.create_activity', {
                  activityName: inputValue,
                })
              }
              styles={selectStyles}
              options={entityOptions}
              grouped
              onCreateOption={onCreateOption}
              onChange={onSelection}
              onBlur={() => handleVisibilityToggle(false)}
              isValidNewOption={() =>
                timesheetEntriesConfig.ADHOC && searchedText !== ''
              }
              searchedText={searchedText}
              onInputChange={(input) => setSearchedText(input)}
            />
          </SelectContainer>
        }
      >
        <WrapperCell onClick={openTaskPopup} isNoEntriesSet={minutes === 0}>
          {getTaskCell(
            task,
            minutes,
            placeHolderText,
            isOnlyProjectsEnabled,
            isProjectOwnerApproval,
          )}
        </WrapperCell>
      </Popover>
    </OutsideClickHandler>
  );
};
